import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Modal, Stack, Text } from '@mantine/core';
import { IconGavel } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TravelRequestFileRow } from '@/module/Travel/Request/components/files/TravelRequestFileRow';
import { useTravelRequestAddFile } from '@/module/Travel/Request/model/TravelRequestQuery';
import {
  TravelRequest,
  TravelRequestFile,
  currentTravelBoardDecisionFiles,
  isTravelApprovalNote,
  sortTravelApprovalSteps,
  travelStepTitle,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { FileInputControl, Form, TextareaControl } from '@/core/components';

import { useAppNotification } from '@feature/utils';
import { DOCUMENT_UPLOAD_ACCEPT, MAX_UPLOAD_SIZE_MB, useUploadNotification } from '@feature/utils/upload/fileUpload';

import { useTravelApprovalApprove, useTravelApprovalReject } from '../model/TravelApprovalQuery';
import { TravelBoardDecisionSchema, travelBoardDecisionSchema } from '../model/TravelApprovalSchema';
import { TravelBoardOutcomeSelectControl } from './form/TravelBoardOutcomeSelectControl';

interface TravelBoardDecisionModalProps {
  request: TravelRequest;
  onClose: () => void;
}

const texts = 'travel.approvals.boardDecision';

export function TravelBoardDecisionModal({ request, onClose }: TravelBoardDecisionModalProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const uploadNotification = useUploadNotification();

  const addFile = useTravelRequestAddFile(request.id);
  const approve = useTravelApprovalApprove(request.id);
  const reject = useTravelApprovalReject(request.id);

  // Holds the upload of this dialog until the refreshed request lists it.
  const [savedFile, setSavedFile] = useState<TravelRequestFile | null>(null);
  const [decisionFailed, setDecisionFailed] = useState(false);
  const [deletedFileIds, setDeletedFileIds] = useState<number[]>([]);

  const savedFileListed = savedFile != null && request.files.some(file => file.id === savedFile.id);
  useEffect(() => {
    if (savedFileListed) {
      setSavedFile(null);
    }
  }, [savedFileListed]);

  const attachedFiles = useMemo(() => {
    const current = currentTravelBoardDecisionFiles(request).filter(file => !deletedFileIds.includes(file.id));

    return savedFile == null || savedFileListed ? current : [...current, savedFile];
  }, [request, savedFile, savedFileListed, deletedFileIds]);
  const fileRequired = attachedFiles.length === 0;

  // A resubmission appends rows, so only each step's latest row counts, matching the activation mail.
  const latestSteps = new Map(sortTravelApprovalSteps(request.approvalSteps).map(step => [step.stepKey, step]));
  const approvalNotes = sortTravelApprovalSteps(request.approvalSteps)
    .filter(step => latestSteps.get(step.stepKey) === step && isTravelApprovalNote(step))
    .sort((a, b) => (a.decidedAt ?? '').localeCompare(b.decidedAt ?? ''));

  const form = useForm<TravelBoardDecisionSchema>({
    resolver: zodResolver(travelBoardDecisionSchema(fileRequired)),
    defaultValues: { outcome: null, file: null, reason: '', note: '' },
  });
  const outcome = useWatch({ control: form.control, name: 'outcome' });
  const { isSubmitting } = form.formState;

  // Awaited calls still resolve after the decided request unmounts this dialog, unlike mutate-level callbacks.
  const handleSubmit = async (values: TravelBoardDecisionSchema) => {
    if (values.outcome == null) {
      return;
    }

    if (fileRequired && values.file != null) {
      let saved: TravelRequestFile;
      try {
        saved = await addFile.mutateAsync({ kind: 'ADMINISTRATIVE_BOARD_DECISION', file: values.file });
      } catch (error) {
        uploadNotification.error(error as Error);
        return;
      }
      setSavedFile(saved);
      setDecisionFailed(false);
      form.setValue('file', null);
    }

    try {
      if (values.outcome === 'approve') {
        await approve.mutateAsync(values.note ? { reason: values.note } : undefined);
      } else {
        await reject.mutateAsync({ reason: values.reason });
      }
    } catch (error) {
      notification.error(error as Error);
      setDecisionFailed(true);
      return;
    }

    notification.success();
    onClose();
  };

  // The request refreshes on its own schedule, so a file deleted here is dropped by hand.
  const handleFileDeleted = (fileId: number) => {
    setDeletedFileIds(ids => [...ids, fileId]);
    setSavedFile(null);
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal
      opened
      centered
      onClose={handleClose}
      closeButtonProps={{ disabled: isSubmitting }}
      title={t(`${texts}.title`)}
      size="lg"
    >
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={handleClose}
        isSubmitting={isSubmitting}
        submitLabel={t(`${texts}.submit`)}
        submitColor={outcome === 'reject' ? 'red' : undefined}
        submitIcon={<IconGavel />}
      >
        <Stack gap="md">
          <Text size="sm">{t(`${texts}.content`)}</Text>
          {approvalNotes.length > 0 && (
            <Stack
              gap="xs"
              role="group"
              aria-label={t('travel.approvals.approvalNote')}
            >
              <Text
                size="sm"
                fw={500}
              >
                {t('travel.approvals.approvalNote')}
              </Text>
              {approvalNotes.map(step => (
                <Stack
                  key={step.id}
                  gap={2}
                >
                  <Text
                    size="xs"
                    c="dimmed"
                  >
                    {[travelStepTitle(step, t), step.approver?.name].filter(Boolean).join(' - ')}
                  </Text>
                  <Text
                    size="sm"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {step.reason}
                  </Text>
                </Stack>
              ))}
            </Stack>
          )}
          {decisionFailed && !fileRequired && <Alert color="red">{t(`${texts}.decisionFailed`)}</Alert>}
          {fileRequired ? (
            <FileInputControl
              name="file"
              label={t('travel.fileKindEnum.ADMINISTRATIVE_BOARD_DECISION')}
              description={t('travel.requests.view.fileHint', { maxSize: MAX_UPLOAD_SIZE_MB })}
              accept={DOCUMENT_UPLOAD_ACCEPT}
              isSubmitting={isSubmitting}
              withAsterisk
              control={form.control}
            />
          ) : (
            <Stack gap="xs">
              {!decisionFailed && <Alert color="blue">{t(`${texts}.attached`)}</Alert>}
              {attachedFiles.map(requestFile => (
                <TravelRequestFileRow
                  key={requestFile.id}
                  requestId={request.id}
                  requestFile={requestFile}
                  activityType={request.activityType}
                  canDelete={!isSubmitting}
                  onDeleted={() => handleFileDeleted(requestFile.id)}
                />
              ))}
            </Stack>
          )}
          <TravelBoardOutcomeSelectControl
            name="outcome"
            label={t(`${texts}.outcome`)}
            isSubmitting={isSubmitting}
            withAsterisk
            control={form.control}
          />
          {outcome === 'approve' && (
            <TextareaControl
              name="note"
              label={t('travel.approvals.approvalNote')}
              isSubmitting={isSubmitting}
              control={form.control}
            />
          )}
          {outcome === 'reject' && (
            <>
              <Text size="sm">{t('travel.approvals.actions.confirmReject.content')}</Text>
              <TextareaControl
                name="reason"
                label={t('travel.approvals.actions.reason')}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
            </>
          )}
        </Stack>
      </Form>
    </Modal>
  );
}
